import React, { useState, useEffect } from "react";
import DocumentUpload from "../../components/DocumentUpload";
import LocationSelectors from "../../components/LocationSelectors";
import { useNavigate, useLocation } from "react-router-dom";
import "../../styles/FormPages.css";

const ConsumerFormPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { country, state: stateVal, district, lawCategory } = location.state || {};

  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 4;

  const [formData, setFormData] = useState({
    country: country || "",
    state: stateVal || "",
    district: district || "",

    complainantFullName: "",
    complainantFatherOrHusband: "",
    complainantAge: "",
    complainantGender: "",
    complainantOccupation: "",
    complainantContact: "",
    complainantAddress: "",
    complainantEmail: "",
    complaintType: "",
    complaintTypeOther: "",
    serviceProvider: "",
    serviceType: "",
    serviceTypeOther: "",
    complaintDetails: "",
    transactionDate: "",
    transactionAmount: "",
    billNumber: "",
    paymentMode: "",
    evidenceDocuments: "",
    deficiency: "",
    lossAmount: "",
    compensationClaimed: "",
    oppositePartyName: "",
    oppositePartyAddress: "",
    oppositePartyContact: "",
    jurisdiction: "",
    declaration: false,
  });

  const [uploadedDocuments, setUploadedDocuments] = useState([]);
  const [extractedContent, setExtractedContent] = useState({});

  const handleDocumentsChange = (files, content) => {
    setUploadedDocuments(files);
    setExtractedContent(content);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentStep]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const nextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const selectedCountry = formData.country || country || "";
    const selectedState = formData.state || stateVal || "";
    const selectedDistrict = formData.district || district || "";
    
    let detailsText = `
👤 Complainant Details:
Full Name: ${formData.complainantFullName}
Father's/Husband's Name: ${formData.complainantFatherOrHusband}
Age: ${formData.complainantAge}
Gender: ${formData.complainantGender}
Occupation: ${formData.complainantOccupation}
Contact: ${formData.complainantContact}
Email: ${formData.complainantEmail}
Address: ${formData.complainantAddress}

🛒 Consumer Complaint Details:
Complaint Type: ${formData.complaintTypeOther || formData.complaintType}
Service Provider: ${formData.serviceProvider}
Service Type: ${formData.serviceTypeOther || formData.serviceType}
Complaint Details: ${formData.complaintDetails}

💰 Transaction Details:
Transaction Date: ${formData.transactionDate}
Transaction Amount: ${formData.transactionAmount}
Bill Number: ${formData.billNumber}
Payment Mode: ${formData.paymentMode}
Evidence Documents: ${formData.evidenceDocuments}

⚖️ Legal Details:
Service Deficiency: ${formData.deficiency}
Loss Amount: ${formData.lossAmount}
Compensation Claimed: ${formData.compensationClaimed}

👥 Opposite Party:
Name: ${formData.oppositePartyName}
Address: ${formData.oppositePartyAddress}
Contact: ${formData.oppositePartyContact}
Jurisdiction: ${formData.jurisdiction}
`.trim();

    if (uploadedDocuments.length > 0) {
      detailsText += `\n\n📄 Supporting Documents:`;
      uploadedDocuments.forEach((doc, idx) => {
        detailsText += `\n\nDocument ${idx + 1}: ${doc.name}`;
        if (extractedContent[doc.id]) {
          detailsText += `\nExtracted Content:\n${extractedContent[doc.id]}`;
        }
      });
    }

    navigate("/drafts", {
      state: {
        query: detailsText,
        summary: detailsText,
        country: selectedCountry,
        state: selectedState,
        district: selectedDistrict,
        lawCategory,
  draftType: "consumer",
  entityName: formData.complainantFullName,
  uploadedDocuments,
  extractedContent,
      },
    });
  };

  const renderStep1 = () => (
    <div className="form-page-section">
      <div className="form-page-section-title">
        👤 Complainant Details
      </div>
      <div className="form-page-row">
        <div className="form-page-col">
          <label>Full Name *</label>
          <input
            type="text"
            name="complainantFullName"
            value={formData.complainantFullName}
            onChange={handleInputChange}
            required
          />
        </div>
        <div className="form-page-col">
          <label>Father's/Husband's Name *</label>
          <input
            type="text"
            name="complainantFatherOrHusband"
            value={formData.complainantFatherOrHusband}
            onChange={handleInputChange}
            required
          />
        </div>
      </div>
      <div className="form-page-row">
        <div className="form-page-col">
          <label>Age *</label>
          <input
            type="number"
            name="complainantAge"
            value={formData.complainantAge}
            onChange={handleInputChange}
            required
          />
        </div>
        <div className="form-page-col">
          <label>Gender *</label>
          <select
            name="complainantGender"
            value={formData.complainantGender}
            onChange={handleInputChange}
            required
          >
            <option value="">Select Gender</option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
            <option value="Other">Other</option>
          </select>
        </div>
      </div>
      <div className="form-page-row">
        <div className="form-page-col">
          <label>Occupation *</label>
          <input
            type="text"
            name="complainantOccupation"
            value={formData.complainantOccupation}
            onChange={handleInputChange}
            required
          />
        </div>
        <div className="form-page-col">
          <label>Contact Number *</label>
          <input
            type="tel"
            name="complainantContact"
            value={formData.complainantContact}
            onChange={handleInputChange}
            required
          />
        </div>
      </div>
      <div className="form-page-row">
        <div className="form-page-col">
          <label>Email ID</label>
          <input
            type="email"
            name="complainantEmail"
            value={formData.complainantEmail}
            onChange={handleInputChange}
          />
        </div>
        <div className="form-page-col">
          <label>Address *</label>
          <textarea
            name="complainantAddress"
            value={formData.complainantAddress}
            onChange={handleInputChange}
            rows={2}
            required
          />
        </div>
      </div>

      <LocationSelectors
        country={formData.country}
        state={formData.state}
        district={formData.district}
        onChange={(next) => setFormData((prev) => ({ ...prev, ...next }))}
      />
    </div>
  );

  const renderStep2 = () => (
    <div className="form-page-section">
      <div className="form-page-section-title">
        🛒 Consumer Complaint Details
      </div>
      <div className="form-page-row">
        <div className="form-page-col">
          <label>Complaint Type *</label>
          <select
            name="complaintType"
            value={formData.complaintType}
            onChange={handleInputChange}
            required
          >
            <option value="">Select Complaint Type</option>
            <option value="Defective Product">Defective Product</option>
            <option value="Poor Service Quality">Poor Service Quality</option>
            <option value="Overcharging">Overcharging</option>
            <option value="False Advertisement">False Advertisement</option>
            <option value="Delay in Service">Delay in Service</option>
            <option value="Warranty/Guarantee Issue">Warranty/Guarantee Issue</option>
            <option value="Unfair Trade Practice">Unfair Trade Practice</option>
            <option value="Other">Other (specify)</option>
          </select>
        </div>
        <div className="form-page-col">
          {formData.complaintType === "Other" && (
            <>
              <label>Specify Complaint Type *</label>
              <input
                type="text"
                name="complaintTypeOther"
                value={formData.complaintTypeOther}
                onChange={handleInputChange}
                required
              />
            </>
          )}
        </div>
      </div>
      <div className="form-page-row">
        <div className="form-page-col">
          <label>Service Provider/Company Name *</label>
          <input
            type="text"
            name="serviceProvider"
            value={formData.serviceProvider}
            onChange={handleInputChange}
            required
          />
        </div>
        <div className="form-page-col">
          <label>Service Type *</label>
          <select
            name="serviceType"
            value={formData.serviceType}
            onChange={handleInputChange}
            required
          >
            <option value="">Select Service Type</option>
            <option value="Banking">Banking</option>
            <option value="Insurance">Insurance</option>
            <option value="Telecom">Telecom</option>
            <option value="E-commerce">E-commerce</option>
            <option value="Automobile">Automobile</option>
            <option value="Electronics">Electronics</option>
            <option value="Healthcare">Healthcare</option>
            <option value="Education">Education</option>
            <option value="Real Estate">Real Estate</option>
            <option value="Food & Beverage">Food & Beverage</option>
            <option value="Travel & Tourism">Travel & Tourism</option>
            <option value="Other">Other (specify)</option>
          </select>
        </div>
      </div>
      {formData.serviceType === "Other" && (
        <div className="form-page-row">
          <div className="form-page-col">
            <label>Specify Service Type *</label>
            <input
              type="text"
              name="serviceTypeOther"
              value={formData.serviceTypeOther}
              onChange={handleInputChange}
              required
            />
          </div>
        </div>
      )}
      <div className="form-page-row">
        <div className="form-page-col full-width">
          <label>Detailed Complaint Description *</label>
          <textarea
            name="complaintDetails"
            value={formData.complaintDetails}
            onChange={handleInputChange}
            rows={4}
            placeholder="Describe your complaint in detail including dates, incidents, and issues faced..."
            required
          />
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="form-page-section">
      <div className="form-page-section-title">
        💰 Transaction & Evidence Details
      </div>
      <div className="form-page-row">
        <div className="form-page-col">
          <label>Transaction Date *</label>
          <input
            type="date"
            name="transactionDate"
            value={formData.transactionDate}
            onChange={handleInputChange}
            required
          />
        </div>
        <div className="form-page-col">
          <label>Transaction Amount (₹) *</label>
          <input
            type="number"
            name="transactionAmount"
            value={formData.transactionAmount}
            onChange={handleInputChange}
            required
          />
        </div>
      </div>
      <div className="form-page-row">
        <div className="form-page-col">
          <label>Bill/Invoice Number</label>
          <input
            type="text"
            name="billNumber"
            value={formData.billNumber}
            onChange={handleInputChange}
          />
        </div>
        <div className="form-page-col">
          <label>Payment Mode *</label>
          <select
            name="paymentMode"
            value={formData.paymentMode}
            onChange={handleInputChange}
            required
          >
            <option value="">Select Payment Mode</option>
            <option value="Cash">Cash</option>
            <option value="Credit Card">Credit Card</option>
            <option value="Debit Card">Debit Card</option>
            <option value="Net Banking">Net Banking</option>
            <option value="UPI">UPI</option>
            <option value="Cheque">Cheque</option>
            <option value="Bank Transfer">Bank Transfer</option>
            <option value="EMI">EMI</option>
          </select>
        </div>
      </div>
      <div className="form-page-row">
        <div className="form-page-col full-width">
          <label>Evidence Documents Available</label>
          <textarea
            name="evidenceDocuments"
            value={formData.evidenceDocuments}
            onChange={handleInputChange}
            rows={3}
            placeholder="List all documents you have as evidence (bills, receipts, communication records, etc.)"
          />
        </div>
      </div>
      <div className="form-page-row">
        <div className="form-page-col full-width">
          <label>Service Deficiency Details *</label>
          <textarea
            name="deficiency"
            value={formData.deficiency}
            onChange={handleInputChange}
            rows={3}
            placeholder="Explain how the service was deficient or the product was defective..."
            required
          />
        </div>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <>
      <div className="form-page-section">
        <div className="form-page-section-title">
          ⚖️ Compensation & Opposite Party Details
        </div>
        <div className="form-page-row">
          <div className="form-page-col">
            <label>Loss/Damage Amount (₹)</label>
            <input
              type="number"
              name="lossAmount"
              value={formData.lossAmount}
              onChange={handleInputChange}
              placeholder="Financial loss suffered"
            />
          </div>
          <div className="form-page-col">
            <label>Compensation Claimed (₹) *</label>
            <input
              type="number"
              name="compensationClaimed"
              value={formData.compensationClaimed}
              onChange={handleInputChange}
              required
            />
          </div>
        </div>
        <div className="form-page-row">
          <div className="form-page-col">
            <label>Opposite Party Name *</label>
            <input
              type="text"
              name="oppositePartyName"
              value={formData.oppositePartyName}
              onChange={handleInputChange}
              required
            />
          </div>
          <div className="form-page-col">
            <label>Opposite Party Contact</label>
            <input
              type="tel"
              name="oppositePartyContact"
              value={formData.oppositePartyContact}
              onChange={handleInputChange}
            />
          </div>
        </div>
        <div className="form-page-row">
          <div className="form-page-col full-width">
            <label>Opposite Party Address *</label>
            <textarea
              name="oppositePartyAddress"
              value={formData.oppositePartyAddress}
              onChange={handleInputChange}
              rows={3}
              required
            />
          </div>
        </div>
        <div className="form-page-row">
          <div className="form-page-col full-width">
            <label>Jurisdiction/Forum *</label>
            <select
              name="jurisdiction"
              value={formData.jurisdiction}
              onChange={handleInputChange}
              required
            >
              <option value="">Select Jurisdiction</option>
              <option value="District Consumer Forum">District Consumer Forum</option>
              <option value="State Consumer Commission">State Consumer Commission</option>
              <option value="National Consumer Commission">National Consumer Commission</option>
            </select>
          </div>
        </div>
      </div>
      <div className="form-page-declaration">
        <div className="form-page-section-title">
          ✅ Declaration
        </div>
        <label>
          <input
            type="checkbox"
            name="declaration"
            checked={formData.declaration}
            onChange={handleInputChange}
            required
          />
          <span>I declare that all the information provided is true and I have not filed a similar complaint elsewhere *</span>
        </label>
      </div>
    </>
  );

  return (
    <div className="form-page-container">
      <div className="form-page-header">
        <a href="#" onClick={() => navigate(-1)} className="form-page-back-link">
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Draft Selection
        </a>
        <h1 className="form-page-title">Create Consumer Complaint</h1>
        <p className="form-page-subtitle">Fill in the details below to generate a professional consumer complaint</p>
      </div>

      <div className="form-page-content">
        <form onSubmit={handleSubmit}>
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
          {currentStep === 4 && renderStep4()}

          {currentStep === totalSteps && (
            <div className="form-page-section">
              <div className="form-page-section-title">📄 Supporting Documents</div>
              <p className="form-page-note">
                Upload bills, receipts, correspondence, photos, or other evidence. Extracted text will be added to your consumer complaint draft.
              </p>
              <DocumentUpload
                onDocumentsChange={handleDocumentsChange}
                maxFiles={8}
                acceptedTypes=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt"
              />
            </div>
          )}

          <div className="form-page-navigation">
            <div className="form-page-step-indicator">
              Step {currentStep} of {totalSteps}
            </div>
            <div className="form-page-buttons">
              {currentStep > 1 && (
                <button
                  type="button"
                  onClick={prevStep}
                  className="form-page-btn form-page-btn-secondary"
                >
                  Previous
                </button>
              )}
              {currentStep < totalSteps ? (
                <button
                  type="button"
                  onClick={nextStep}
                  className="form-page-btn form-page-btn-primary"
                >
                  Next
                </button>
              ) : (
                <button
                  type="submit"
                  className="form-page-btn form-page-btn-primary"
                >
                  Generate Consumer Complaint
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ConsumerFormPage;
